/* engine/golaRuns.js — GOLA 3D: the handle-less profile that runs BETWEEN the
 * modules (2026-08-15 user mandate: "the gola glb we will upload, that we
 * place between modules … it can be set automatically but manually too, like
 * we increase height/width and a snap thing to place it there").
 *
 * MODEL
 *  · The technician uploads a .glb on a Gola profile (Material Library → Gola
 *    → Attach 3D). It is a PROFILE cross-section: the run stretches it along
 *    its length, never along its section.
 *  · The customer picks one for the room (Room → Gola profile). That writes
 *    kitchenWizard.roomGola = <profile name>; the runs are DERIVED, so adding
 *    or moving a cabinet re-flows them automatically (nothing to re-place by
 *    hand — "it can be set automatically").
 *  · kitchenWizard.golaAdjust = { yMm, dMm, hMm, snap } is the MANUAL half:
 *    height off the floor, depth inset from the front face, profile height,
 *    and a snap flag that locks the run to the top of the base carcasses.
 *    Purely additive — absent = the automatic placement.
 *
 * Pure module: no THREE, no state mutation. buildGolaRuns() returns plain run
 * descriptors that scene3d/cabinetMath turns into spec parts (role "gola",
 * carrying the profile's model url so the loaders fit the real geometry).
 */

/** Max gap (m) between two base modules that still counts as ONE run — the
 *  same tolerance the worktop runs use, so gola and worktop break together. */
import { resolveEntry } from "./catalogIds";

export const GOLA_RUN_GAP_M = 0.02;

/** Max gap (m) between two FACING vertical golas that still stitch into ONE
 *  post. It MUST cover golaClearance.GOLA_SIDE_CLEARANCE_M (30mm) plus its
 *  capture tolerance (12mm): a gola side pushes its neighbour to exactly that
 *  30mm keep-out, so with the run gap (20mm) the standard adjacency NEVER
 *  merged — two 67mm profiles rendered on top of each other in the same
 *  30mm gap (measured 2026-08-26: posts at s = 1.400 and s = 1.430, spans
 *  1.382..1.449 and 1.381..1.448). That doubled, z-fighting geometry is one
 *  of the "pieces" the user sees, and it is why moving a gola from Left to
 *  Right looks like nothing moved — the neighbour's profile was already
 *  standing there. */
export const GOLA_POST_STITCH_M = 0.045;

/** Default profile section when the model does not declare one (mm). */
export const GOLA_DEFAULT_H_MM = 38;
export const GOLA_DEFAULT_D_MM = 16;
/** Vertical gola channel width along the wall (mm). */
export const GOLA_POST_W_MM = 38;
/** Gola face plane: the profile's front face sits HERE behind the carcass
 *  front — 22mm = the DOOR BACK plane, where the authored cut opens
 *  (2026-08-22 user: "push the gola back to the cut to fill the gap").
 *  The fronts give up the strip on a gola side, so the profile stays
 *  VISIBLE beside them, one step deeper than the door face. Vertical posts
 *  seat on the same plane (cabinetMath q = qFront − 0.022 − wSec/2) —
 *  coplanar, flush joints. */
export const GOLA_FACE_RECESS_M = 0.022;

/** golaProfileByName(blob, name) → the technician entry, or null. */
export function golaProfileByName(blob, name) {
  if (!name) return null;
  const list = (blob && blob.gola) || [];
  return list.find((g) => g && g.name === name) || null;
}

/** golaProfileById(blob, "gola-K" | "gola-u<uid>") → the entry, or null.
 *  STABLE IDS (2026-08-22): uid form finds the profile wherever it moved;
 *  the index form stays the legacy read-alias. */
export function golaProfileById(blob, id) {
  return resolveEntry((blob && blob.gola) || [], id, "gola");
}

/** The uploaded file's own orientation + section, from the parse-time meta
 *  the technician upload stored (modelMeta.sizeMm). The user's profiles are
 *  authored VERTICALLY (h ≈ 19,000mm dominant, section 48×20mm) — that FACT
 *  now decides where the 3D goes: a vertical file mounts ONLY on the vertical
 *  posts; only a horizontally-authored file rides the horizontal rail.
 *  (2026-08-19 user: "the golas I uploaded are vertical — you put vertical
 *  AND horizontal, check the glb, make sure it's them!") */
export function golaOrientOf(profile) {
  const sz = profile && profile.modelMeta && profile.modelMeta.sizeMm;
  if (!sz || !(sz.w > 0) || !(sz.h > 0) || !(sz.d > 0)) return null;
  const mx = Math.max(sz.w, sz.h, sz.d);
  if (mx < 200) return null;                       // not an extruded profile
  return mx === sz.h ? "vertical" : "horizontal";
}

/** The file's REAL cross-section in metres ({secH, secD}, larger side first),
 *  so the mounted profile keeps ITS authored size — clamped to 10..90mm. */
export function golaSectionOf(profile) {
  const sz = profile && profile.modelMeta && profile.modelMeta.sizeMm;
  if (!sz) return null;
  let dims = [sz.w || 0, sz.h || 0, sz.d || 0].sort((a, b) => a - b);
  if (!(dims[0] > 0) || !(dims[1] > 0)) return null;
  // UNIT-SLIP NORMALISATION: CAD exports are often 10x off (cm authored as
  // mm — the user's file reads 483x201mm section, really 48x20). A gola face
  // over 90mm is not a gola: divide by 10 until it is plausible.
  let guard = 0;
  while (dims[1] > 90 && guard++ < 3) dims = dims.map((v) => v / 10);
  const cl = (v) => Math.min(0.09, Math.max(0.008, v / 1000));
  return { secH: cl(dims[1]), secD: cl(dims[0]) };   // mid = face, small = depth
}

/** golaFitOf(profile) → { rot, offMm, depMm } — the TECHNICIAN's per-profile
 *  seat tuning (2026-08-22 user: "at Cabinet Technician add rotate gola and
 *  move left/right, back/front by 1mm … saved at that specific place — and
 *  when a user chooses gola in the configurator it is set just like I set it
 *  here"). Stored on the blob's gola entry as entry.fit; absent = zeros (the
 *  baked smart seat alone — the tuned single-profile fit). */
export function golaFitOf(profile) {
  const f = (profile && profile.fit) || {};
  const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
  /* SEAT v3 (2026-08-26): the lateral / depth nudges are deltas on the SEAT,
   * and the seat itself changed (the profile now sits on the gable's own
   * front, its wide face across the wall — the working demo's rule). Numbers
   * tuned against the OLD seat were compensation for a wrong anchor: applying
   * them now would just re-break the placement. They are honoured only once
   * re-entered in Cabinet Technician, which stamps fit.v = 3. Rotation is
   * seat-independent, so it always rides along. */
  const live = Number(f.v) >= 3;
  return {
    rot: ((Math.round(num(f.rot)) % 360) + 360) % 360,
    offMm: live ? num(f.offMm) : 0,
    depMm: live ? num(f.depMm) : 0,
    stale: !live && (num(f.offMm) !== 0 || num(f.depMm) !== 0),
  };
}

/** GOLA_FIT_VERSION — stamped on entry.fit whenever the technician edits a
 *  nudge, so the engine knows the value was authored against THIS seat. */
export const GOLA_FIT_VERSION = 3;

/** golaProfileIsExtruded(profile) — is the attached .glb actually a PROFILE?
 *  (2026-08-26: three of the live gola entries carry a whole 249-mesh kitchen
 *  export — "Gola Single Sided 48mm.glb", 7.1 x 14.9 x 2.3 m. Fitted as a
 *  profile that squashes an entire room into a 20mm sliver, which is exactly
 *  the "it's showing pieces on 3D" the user reported.) An extrusion has ONE
 *  dominant axis: length >= 5x its own section. */
export function golaProfileIsExtruded(profile) {
  const sz = profile && profile.modelMeta && profile.modelMeta.sizeMm;
  if (!sz) return true;                       // no meta = no evidence against it
  const dims = [sz.w || 0, sz.h || 0, sz.d || 0].sort((a, b) => a - b);
  if (!(dims[1] > 0)) return true;
  return dims[2] >= dims[1] * 5;
}

/** golaGeomOf(blob, profile) → { entry, model, acrossM, depM } — the geometry
 *  that ACTUALLY renders for a pick, resolved once for every view.
 *  · model: the profile's own .glb when it is a real extrusion, else the
 *    library's first valid profile (the technician lists finish variants
 *    beside one uploaded profile — and it heals a mis-attached file).
 *  · acrossM / depM: the REAL cross-section of THAT model, in metres —
 *    wide face ACROSS the wall, thin section BACK from the front, exactly
 *    like the demo (CROSS_SCALE 1:1 mm, never a squashed envelope). */
export function golaGeomOf(blob, profile) {
  const list = (blob && blob.gola) || [];
  /* THE MODEL: the entry's own file always, now that the loader can pull a
   * profile OUT of a bigger export (glbAssembly.extractGolaProfile). His gola
   * uploads are whole SketchUp kitchens with the real profile inside them —
   * Mesh.030 is 17 x 66 x 2200, Mesh.032 is 17 x 48 x 2200, exactly his
   * dimensioned drawings — so rejecting the file threw away geometry he had
   * actually uploaded. */
  const entry = (profile && profile.model) ? profile
    : (list.find((g) => g && g.model) || null);
  /* THE SECTION. Stored modelMeta is measured over the WHOLE file, so for one
   * of those exports it reads 71 x 23mm — the kitchen's bounding box, not the
   * profile. When the metadata is not a plausible extrusion, fall back to the
   * section the technician named the entry for; his own drawings define them:
   * double = 66 x 17mm, single = 48 x 17mm. A clean single-profile upload
   * measures itself and never reaches this. */
  const sec = golaProfileIsExtruded(entry) ? golaSectionOf(entry) : null;
  const named = sec ? null : golaNamedSection(entry || profile);
  return {
    entry,
    model: (entry && entry.model) || null,
    substituted: !!(entry && profile && entry !== profile),
    measured: !!sec,
    acrossM: sec ? sec.secH : (named ? named.secH : GOLA_POST_W_MM / 1000),
    depM: sec ? sec.secD : (named ? named.secD : GOLA_DEFAULT_D_MM / 1000),
  };
}

/** golaNamedSection(entry) — the section his drawings give for a profile the
 *  stored metadata cannot describe. Double 66 x 17mm, single 48 x 17mm. */
export function golaNamedSection(entry) {
  const n = String((entry && (entry.name || entry.modelName)) || "");
  if (/double|dopio|66/i.test(n)) return { secH: 0.066, secD: 0.017 };
  if (/single|teke|48/i.test(n)) return { secH: 0.048, secD: 0.017 };
  return null;
}

/** Gable thickness (m) the profile clips over — the standard 18mm side panel,
 *  the same 18 the rebate is cut at (golaClearance.GOLA_NOTCH_MM). */
export const GOLA_GABLE_T_M = 0.018;

/** golaPostSeat(po, blob) — THE one seat rule for a vertical gola post
 *  (2026-08-23 "one source of truth" mandate: Plan + Elevation + 3D must
 *  consume the SAME gola geometry).
 *
 *  SEAT v3 (2026-08-26) — ported from the working demo the user supplied,
 *  whose attachGolaHardware() is the reference. Its three anchors, verified
 *  against the real uploads ("Gola Teke Vertikale.glb": local X 0..48.26 =
 *  width, local Y -20.07..0 = depth, local Z -1899.92..0 = height):
 *    · SECTION — the file's WIDE face (48.3 / 66.8mm) runs ACROSS the wall
 *      and its THIN section (20.1mm) runs BACK from the front, 1:1 mm. The
 *      old rule had these two swapped, so a 20mm sliver stood across the
 *      pocket and the 48mm face was buried 71mm inside the carcass — the
 *      profile was physically there and invisible from the room.
 *    · DEPTH — "p0z = the panel's TRUE, pre-notch front": the profile's FRONT
 *      face is flush with the gable's front = the door-back plane
 *      (qFront - GOLA_FACE_RECESS_M), and its body extrudes BACKWARD from it.
 *    · LATERAL — "p0x = the panel's INSIDE face", body extruding OUTWARD
 *      (mirrored on the left). So the profile clips over the gable's front
 *      edge: 18mm of it on the cabinet, the rest spanning the joint — which
 *      is what makes Left and Right land on visibly opposite sides. The old
 *      baked +/-13mm smart offsets put BOTH sides fully outside the module
 *      (measured: L across 973..997mm, R 1603..1627mm on a 1000..1600 unit),
 *      which is the "I click right, it stays on the left" report.
 *    · A stitched joint post ("J", two facing golas) stays CENTRED on the
 *      boundary — the demo's double-sided "between 2 cabinets" case.
 *  Returns dSec = the ACROSS width and wSec = the BACKWARD depth (the field
 *  names every consumer already reads), plus `mirror` for the left-hand
 *  instance. */
export function golaPostSeat(po, blob, opts) {
  const profile = golaProfileByName(blob, po.profile) || golaProfileById(blob, po.profile);
  const geom = golaGeomOf(blob, profile);
  const dSec = geom.acrossM;                             // ACROSS the wall
  const wSec = geom.depM;                                // BACK from the front
  /* NO PER-PROFILE NUDGES (2026-08-26 user: "remove this now, we don't need
   * this now bcs in configurator is working"). Rotation / Offset / Depth were
   * hand-corrections for a seat that could not place itself. The seat is now
   * fully DERIVED — the profile is measured out of its own .glb and lands on
   * the gable the notch pass measured — so a stored nudge can only push it
   * back off. One live entry still carries offMm: 1 from that era; ignoring it
   * is what keeps the two sides identical. Turning a profile is a per-module
   * choice and lives in the configurator's Gola tab (config.golaRot). */
  const offM = 0;
  const sIn = po.side === "L" ? po.s + GOLA_GABLE_T_M - dSec / 2
    : po.side === "R" ? po.s - GOLA_GABLE_T_M + dSec / 2
      : po.s;
  const q = po.qFront - GOLA_FACE_RECESS_M - wSec / 2;
  const botM = po.botM, topM = po.topM;
  return {
    profile, wSec, dSec, model: geom.model, substituted: geom.substituted,
    /* PER-INSTANCE ROTATION (2026-08-26 user: "gola should face me not behind
     * — either rotate them or add a rotate button"): a quarter-turn about the
     * profile's own long axis, on top of the technician's per-profile fit.
     * Stored as config.golaRot so one cabinet can differ from the next. */
    secRot: (((opts && Number(opts.rotDeg)) || 0) % 360 + 360) % 360,
    offM, depM: 0,
    mirror: po.side === "L",
    sIn, q, botM, topM,
    lenM: Math.max(0.05, topM - botM),
  };
}

/** golaPlanRects(p, def, placements, effDefOf, blob) — the vertical-gola
 *  footprint(s) of ONE module in MODULE-LOCAL plan metres (x centre-0/+right,
 *  z centre-0/+front — the cabinetCuts frame PlanView draws in), derived from
 *  the SAME golaPostSeat rule the 3D and elevation use. Mirrors
 *  buildGolaPosts' stitch: two facing golas within GOLA_RUN_GAP_M become ONE
 *  joint post, drawn by the LEFT member only (centred on the boundary). */
export function golaPlanRects(p, def, placements, effDefOf, blob) {
  const out = [];
  if (!p || !def) return out;
  if (def.role !== "cabinet-base" && def.role !== "cabinet-tall") return out;
  const cfg = p.config || {};
  const pos = String(cfg.golaPos || "");
  if (pos !== "L" && pos !== "R" && pos !== "LR") return out;
  const profile = golaProfileById(blob, cfg.gola);
  if (!profile) return out;
  const W = def.width || 0, D = def.depth || 0;
  const a0 = Number(p.absLeft) || 0, a1 = a0 + W;
  const facing = (side) => (placements || []).find((q) => {
    if (!q || q === p || q.id === p.id || !q.wallId || !p.wallId || q.wallId !== p.wallId) return false;
    const qd = effDefOf ? effDefOf(q) : null;
    if (!qd || (qd.role !== "cabinet-base" && qd.role !== "cabinet-tall")) return false;
    const qc = q.config || {};
    if (!qc.gola || qc.gola === "None") return false;
    const qpos = String(qc.golaPos || "");
    const b0 = Number(q.absLeft) || 0, b1 = b0 + (qd.width || 0);
    if (side === "R") return (qpos === "L" || qpos === "LR") && b0 >= a1 - 1e-6 && b0 - a1 <= GOLA_POST_STITCH_M;
    return (qpos === "R" || qpos === "LR") && b1 <= a0 + 1e-6 && a0 - b1 <= GOLA_POST_STITCH_M;
  });
  const mk = (side) => {
    const nb = facing(side);
    if (nb && side === "L") return;   // right member of a joint yields to the left one
    const edge = side === "L" ? -W / 2 : W / 2;
    const s = nb ? (W / 2 + ((Number(nb.absLeft) || 0) - a1) / 2) : edge;
    const po = { side: nb ? "J" : side, s, qFront: D / 2, botM: 0, topM: 1, profile: profile.name };
    const seat = golaPostSeat(po, blob);
    out.push({
      x1: seat.sIn - seat.dSec / 2, x2: seat.sIn + seat.dSec / 2,
      z1: seat.q - seat.wSec / 2, z2: seat.q + seat.wSec / 2,
      side: po.side,
    });
  };
  if (pos === "L" || pos === "LR") mk("L");
  if (pos === "R" || pos === "LR") mk("R");
  return out;
}

/** golaModelFor(blob, profile) → the 3D to run for a profile pick.
 *  The technician typically uploads ONE .glb and lists several FINISH
 *  variants (Champagne, Brushed Brass…) beside it — picking a finish without
 *  its own model must still run the uploaded profile ("I go gola and still it
 *  doesn't attach the 3D I put in Cabinet Technician", 2026-08-18). Own model
 *  wins; else the library's first profile WITH a model; else null (plain
 *  channel box). */
export function golaModelFor(blob, profile) {
  // 2026-08-26: a model that is NOT an extruded profile is skipped here too —
  // squashing a full-kitchen export into a 20mm post is what rendered as
  // "pieces". golaGeomOf holds the one resolution rule.
  return golaGeomOf(blob, profile).model;
}

/** Normalised manual adjustments (mm), defensive against junk. */
export function golaAdjustOf(wizard) {
  const a = (wizard && wizard.golaAdjust) || {};
  const num = (v, dflt) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : dflt;
  };
  return {
    yMm: num(a.yMm, 0),                                 // raise / lower
    dMm: num(a.dMm, 0),                                 // push in / pull out
    hMm: Math.max(4, num(a.hMm, GOLA_DEFAULT_H_MM)),    // profile height
    snap: a.snap !== false,                             // lock to the carcass top
  };
}

/**
 * buildGolaRuns({ placements, lib, wizard, blob, effDef })
 *  → [{ id, wallId, profile, model, s0, s1, lengthM, members, yM, dM, hM }]
 *
 * One run per contiguous group of BASE modules on a wall (a single module is
 * still a run — a gola exists on one cabinet too, unlike a worktop seam).
 * Coordinates are the wall's own axis (s = along the wall), left for the
 * scene layer to map back through each module's angle, exactly like the
 * worktop runs do.
 */
export function buildGolaRuns(opts) {
  const { placements, lib, wizard, blob, effDef, geomOf } = opts || {};
  const name = (wizard && wizard.roomGola) || null;
  const profile = golaProfileByName(blob, name);
  if (!profile || !profile.model) return [];            // no 3D profile → nothing
  const adj = golaAdjustOf(wizard);

  const items = [];
  (placements || []).forEach((p) => {
    if (!p || !p.wallId) return;
    const def = effDef ? effDef(p, lib) : (lib || {})[p.type];
    if (!def || def.role !== "cabinet-base") return;
    const g = geomOf ? geomOf(p, def) : null;
    if (!g) return;
    items.push({
      id: p.id, wallId: p.wallId,
      s0: g.s - g.w / 2, s1: g.s + g.w / 2,
      q: g.q, angle: g.angle || 0,
      topM: g.topM, depthM: g.depthM,
    });
  });
  if (!items.length) return [];

  const buckets = new Map();
  items.forEach((it) => {
    const key = it.wallId + "|" + Math.round(it.q * 1000) + "|" + Math.round(it.topM * 1000);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(it);
  });

  const runs = [];
  buckets.forEach((arr) => {
    arr.sort((a, b) => a.s0 - b.s0);
    let cur = null;
    const flush = () => { if (cur) runs.push(cur); cur = null; };
    arr.forEach((it) => {
      if (cur && it.s0 - cur.s1 <= GOLA_RUN_GAP_M) {
        cur.s1 = Math.max(cur.s1, it.s1);
        cur.members.push(it.id);
      } else {
        flush();
        cur = {
          wallId: it.wallId, q: it.q, angle: it.angle, topM: it.topM, depthM: it.depthM,
          s0: it.s0, s1: it.s1, members: [it.id],
        };
      }
    });
    flush();
  });

  runs.sort((a, b) => (a.wallId < b.wallId ? -1 : a.wallId > b.wallId ? 1 : a.s0 - b.s0));
  // ROTATED SECTION (2026-08-22 user: "rotate gola"): the horizontal run lies
  // in the SAME orientation family as the vertical post's measured pocket
  // seat — the THIN side of the profile is the visible vertical lip at the
  // front, the WIDE leg runs BACKWARD under the worktop, hidden (the SketchUp
  // fit). The old mapping stood the WIDE face tall at the front — the profile
  // read 90° wrong. File section wins (secD thin, secH wide); defaults keep
  // the manual hMm as the hidden leg depth.
  const sec = golaSectionOf(profile);
  const hM = sec ? sec.secD : GOLA_DEFAULT_D_MM / 1000;
  const dM = sec ? sec.secH : adj.hMm / 1000;
  return runs.map((r, i) => {
    // SNAP (default): the profile sits directly under the worktop, its top
    // flush with the carcass top. Snap off → the manual y is absolute-ish,
    // still measured from the carcass top so a nudge stays predictable.
    const yTop = r.topM + adj.yMm / 1000;
    return {
      id: "__gola__" + i,
      wallId: r.wallId,
      profile: profile.name,
      model: profile.model,
      members: r.members.slice(),
      angle: r.angle,
      s0: r.s0, s1: r.s1,
      lengthM: Math.round((r.s1 - r.s0) * 1000) / 1000,
      // AT THE FRONT (2026-08-18 fix — "I uploaded the 3D gola and it still
      // doesn't work"): r.q is the member CENTRE, so the profile used to
      // render buried inside the carcass. The wall side is q − depth/2, the
      // front face q + depth/2. The channel face sits on the SAME plane as
      // the per-module recessed rail (front − 12mm, cabinetMath's _fd plane)
      // so run, rail and vertical posts all join flush. Positive dMm insets
      // toward the wall (smaller q).
      q: r.q + r.depthM / 2 - GOLA_FACE_RECESS_M - dM / 2 - (adj.dMm / 1000),
      yM: yTop - hM / 2,                                // part CENTRE
      hM,
      dM,
      snap: adj.snap,
    };
  });
}

/**
 * buildGolaPosts({ placements, lib, blob, effDef, geomOf })
 *  → [{ id, wallId, angle, s, qFront, botM, topM, profile, model, members }]
 *
 * VERTICAL golas (2026-08-18 user mandate: "when we click on a gola profile a
 * dropdown shows up so we can put the gola LEFT / RIGHT — double gola too —
 * and in 3D it stitches by module itself like a smart system").
 *
 *  · config.gola     = "gola-K" — the profile pick (unchanged, still priced).
 *  · config.golaPos  = "L" | "R" | "LR" — which side(s) of THIS module carry
 *    a vertical channel. Absent = none (legacy configs render exactly as
 *    before). "LR" = double gola.
 *  · SMART STITCH: two neighbours requesting a channel at their shared edge
 *    (module A right + module B left) merge into ONE post — never two
 *    overlapping profiles. Merge tolerance = GOLA_RUN_GAP_M, like the runs.
 *  · The post spans the module's carcass (bottom→top) at its FRONT face; a
 *    profile without a .glb still renders as a plain channel box.
 */
export function buildGolaPosts(opts) {
  const { placements, lib, blob, effDef, geomOf } = opts || {};
  const req = [];
  (placements || []).forEach((p) => {
    // wall-less modules (islands, the module-editor preview) post too —
    // they bucket under "__free__" per angle, so island neighbours still stitch
    if (!p) return;
    const cfg = p.config || {};
    const pos = String(cfg.golaPos || "");
    if (pos !== "L" && pos !== "R" && pos !== "LR") return;
    const rotDeg = ((Math.round(Number(cfg.golaRot) || 0) % 360) + 360) % 360;
    const profile = golaProfileById(blob, cfg.gola);
    if (!profile) return;
    const def = effDef ? effDef(p, lib) : (lib || {})[p.type];
    if (!def || (def.role !== "cabinet-base" && def.role !== "cabinet-tall")) return;
    const g = geomOf ? geomOf(p, def) : null;
    if (!g) return;
    // topRealM (when the caller provides it) is the carcass part's ACTUAL top
    // — a module raised/lowered via position.elevation keeps its post spanning
    // the real carcass, not the nominal def.height.
    const topM = g.topRealM != null ? g.topRealM : (g.topM || 0);
    /* BOTTOM (batch 9, 2026-09-28 user: "add a button on Edit Module for gola: if the user
     * wants the gola to go down to the floor he can click it; if not it stops normally at the
     * end of that module"): the seat stays the module's own bottom (plinth top) unless
     * cfg.golaBottom === "floor" — then the post runs to the world floor. */
    /* BATCH 10 (2026-09-29 user: "still gola until floor doesn't work"): the seat is the
     * module's own DOOR bottom when the spec knows it (geomOf.frontBotM — the leaf's lower
     * edge, above any plinth), else the plinth top, else the body bottom. It used to lean on
     * the scene's cosmetic door aligner, which silently never ran for one of his uploaded
     * talls — that post stood floor → top while its twin sat on its door. "Down to floor" is
     * the world floor and nothing may re-seat it afterwards (toFloor rides on the post). */
    const botBody = g.botM != null ? g.botM : Math.max(0, topM - 0.68);
    // a module WITH an engine kickboard keeps the plinth-top seat the drawings pin (2026-08-24);
    // a module without one (talls, uploads with their own base) seats on its lowest front
    const botSeat = g.kickTopM == null && g.frontBotM != null && g.frontBotM > botBody + 0.001 && g.frontBotM < topM - 0.2 ? g.frontBotM : botBody;
    const toFloor = cfg.golaBottom === "floor";
    const botDefault = toFloor ? 0 : botSeat;
    /* GOLA HEIGHT (2026-08-30 user: "add height … accepting math equations;
     * only UP, not down — down is perfect; and an AUTO that connects the
     * gola to the wall units on top"). Two per-module overrides, both
     * extending the TOP only (the seated bottom never moves):
     *   · cfg.golaHmm  — explicit total height in mm (the Edit Module input
     *     evaluates "2000+240"-style expressions into this number);
     *   · cfg.golaTop === "wall" — LIVE connect: the top extends to the
     *     BOTTOM of the wall-hung units above this module's span, re-derived
     *     on every rebuild, so moving the wall units keeps them connected.
     * Either sets fixedTop, which tells the door-height aligner to leave the
     * profile's length alone (X/Z seating still applies — same X·Y logic). */
    let topOv = null;
    const hOv = Number(cfg.golaHmm) || 0;
    if (hOv > 0) topOv = botDefault + hOv / 1000;
    else if (cfg.golaTop === "wall") {
      /* AUTO (2026-08-30b, measured on his stack): "it detects if there is a
       * unit on top and the gola puts itself". GEOMETRIC detection — any-ROLE
       * module above (his stacked boxes are custom types), same wall, span
       * overlap, sitting at/above this module's top — and the target is the
       * uppers' TOP: the channel runs THROUGH the stacked units so tall +
       * stack read as one column with one continuous gola. */
      const s0 = g.s - g.w / 2, s1 = g.s + g.w / 2;
      let upTop = null;
      (placements || []).forEach((q) => {
        if (!q || q === p || q.id === p.id) return;
        if ((q.wallId || "__free__") !== (p.wallId || "__free__")) return;
        const qd = effDef ? effDef(q, lib) : (lib || {})[q.type];
        if (!qd) return;
        const qg = geomOf ? geomOf(q, qd) : null;
        if (!qg) return;
        const q0 = qg.s - qg.w / 2, q1 = qg.s + qg.w / 2;
        if (Math.min(s1, q1) - Math.max(s0, q0) <= 0.001) return;
        /* FRAME TRAP (2026-08-30b, live-measured): geomOf's topM is the
         * NOMINAL def height while botM is WORLD — mixing them made a wall
         * unit's "top" 0.6m and the connect never fired. World throughout:
         * topRealM when the caller provides it, else world bottom + height. */
        const qBot = qg.botM != null ? qg.botM : ((qg.topRealM || qg.topM || 0) - ((qd && qd.height) || 0));
        if (qBot < topM - 0.15) return;                  // not an "on top" unit
        const qTop = qg.topRealM != null ? qg.topRealM : (qBot + ((qd && qd.height) || 0));
        upTop = upTop == null ? qTop : Math.max(upTop, qTop);
      });
      if (upTop != null && upTop > topM + 0.001) topOv = upTop;
    }
    const mk = (s, side) => req.push({
      wallId: p.wallId || "__free__", angle: g.angle || 0,
      s, side, qFront: g.q + (g.depthM || 0) / 2,
      botM: botDefault,
      topM: topOv != null ? topOv : topM,
      fixedTop: topOv != null,
      toFloor,
      profile: profile.name, model: golaModelFor(blob, profile),
      rotDeg,
      member: p.id,
    });
    if (pos === "L" || pos === "LR") mk(g.s - g.w / 2, "L");
    if (pos === "R" || pos === "LR") mk(g.s + g.w / 2, "R");
  });
  if (!req.length) return [];

  // stitch: same wall + same angle, posts within the run gap merge into one
  const walls = new Map();
  req.forEach((r) => {
    const key = r.wallId + "|" + Math.round((r.angle || 0) * 1000);
    if (!walls.has(key)) walls.set(key, []);
    walls.get(key).push(r);
  });
  const posts = [];
  walls.forEach((arr) => {
    arr.sort((a, b) => a.s - b.s);
    let cur = null;
    const flush = () => { if (cur) posts.push(cur); cur = null; };
    arr.forEach((r) => {
      if (cur && r.s - cur.s <= GOLA_POST_STITCH_M) {
        // shared boundary → ONE post: deepest front wins, height spans both.
        // side "J" = a JOINT post between two modules (centred on the joint,
        // not clipped onto one panel edge).
        cur.s = (cur.s + r.s) / 2;
        if (cur.side !== r.side) cur.side = "J";
        cur.qFront = Math.max(cur.qFront, r.qFront);
        cur.botM = Math.min(cur.botM, r.botM);
        cur.topM = Math.max(cur.topM, r.topM);
        cur.fixedTop = !!(cur.fixedTop || r.fixedTop);   // an extended member fixes the joint post too
        cur.toFloor = !!(cur.toFloor || r.toFloor);      // one member down to the floor takes the joint post with it
        if (!cur.model && r.model) { cur.model = r.model; cur.profile = r.profile; }
        if (!cur.rotDeg && r.rotDeg) cur.rotDeg = r.rotDeg;
        if (cur.members.indexOf(r.member) < 0) cur.members.push(r.member);
      } else {
        flush();
        cur = { ...r, members: [r.member] };
      }
    });
    flush();
  });
  posts.sort((a, b) => (a.wallId < b.wallId ? -1 : a.wallId > b.wallId ? 1 : a.s - b.s));
  /* CORNER STITCH (2026-08-26 "make the gola fully auto notch work on the
   * corner"): the pass above buckets by wall + angle, so two runs meeting at
   * an inside corner NEVER merged — they are on different walls at different
   * angles, and the two perpendicular profiles rendered on top of each other
   * in the same corner. Perpendicular profiles cannot be merged into one, so
   * the corner keeps exactly ONE: posts whose WORLD anchors coincide collapse
   * to a single member-merged post. Distances are in world metres, so this is
   * blind to walls, angles and islands alike. */
  const world = (po) => {
    const c = Math.cos(po.angle || 0), sn = Math.sin(po.angle || 0);
    const q = po.qFront - GOLA_FACE_RECESS_M;
    return { x: c * po.s + sn * q, z: -sn * po.s + c * q };
  };
  const kept = [];
  const angDiff = (a, b) => {
    const d = Math.abs(((a - b) + Math.PI) % (2 * Math.PI) - Math.PI);
    return Math.min(d, Math.PI - d);
  };
  posts.forEach((po) => {
    const w = world(po);
    /* only a REAL corner merges across buckets: the two profiles must be at
     * an angle to each other. Two PARALLEL posts that happen to share a spot
     * are an island sitting on a wall run, not a corner — the wall/island
     * separation stays exactly as it was. */
    const hit = kept.find((k) => Math.hypot(k.w.x - w.x, k.w.z - w.z) <= GOLA_POST_STITCH_M
      && angDiff(k.po.angle || 0, po.angle || 0) > 0.26);
    if (!hit) { kept.push({ po, w }); return; }
    // one profile in the corner: deepest front, tallest span, members merged
    hit.po.qFront = Math.max(hit.po.qFront, po.qFront);
    hit.po.botM = Math.min(hit.po.botM, po.botM);
    hit.po.topM = Math.max(hit.po.topM, po.topM);
    hit.po.fixedTop = !!(hit.po.fixedTop || po.fixedTop);
    hit.po.toFloor = !!(hit.po.toFloor || po.toFloor);
    if (hit.po.side !== po.side) hit.po.side = "J";
    if (!hit.po.model && po.model) { hit.po.model = po.model; hit.po.profile = po.profile; }
    po.members.forEach((m) => { if (hit.po.members.indexOf(m) < 0) hit.po.members.push(m); });
  });
  return kept.map(({ po }) => po).map((po, i) => ({
    id: "__golapost__" + i,
    wallId: po.wallId, angle: po.angle,
    s: po.s, side: po.side || "J", qFront: po.qFront,
    botM: po.botM, topM: po.topM,
    ...(po.fixedTop ? { fixedTop: true } : {}),
    ...(po.toFloor ? { toFloor: true } : {}),
    profile: po.profile, model: po.model, rotDeg: po.rotDeg || 0,
    members: po.members.slice(),
  }));
}
